/* @flow */
/* eslint-env es6,browser */

import adconfig from '../adconfig';
import adloader from '../adloader';
import containerHelper from '../container-helper';
import _adserverbase from './adserverbase';
import { BrowserUtil, isJSONSupported } from '../utils/browserutil';
import { logDebug, logError } from '../utils/logger';
import isdebugEnabled from '../utils/debug';
import { extend, isFunction, param } from '../utils/coreutils';
import { getRandom } from '../utils';
import { cleanFrame } from '../utils/iframeutils';
import { getEncryptedIDP } from '../idproviders/idputils';

interface googleSlot {
  // eslint-disable-next-line no-use-before-define
  addService(googleService): googleSlot;
  setTargeting(string, mixed): googleSlot;
  getSlotElementId(): string;
  setForceSafeFrame(boolean): googleSlot;
  setSafeFrameConfig(mixed): googleSlot;
}

interface googleEvent {
  slot: googleSlot;
}

interface googleService {
  disableInitialLoad(): void;
  enableSingleRequest(): boolean;
  getSlots(): Array<googleSlot>;
  setTargeting(string, mixed): googleService;
  addEventListener(string, (googleEvent) => void): googleService;
}

declare type encryptedSignalSource = {
  id: string,
  collectorFunction: () => Promise<string>
};

interface googleGPT {
  encryptedSignalProviders: Array<encryptedSignalSource>;
  cmd: Array<cbFunc>;
  openConsole(): void;
  display(string): void;
  enableServices(): void;
  destroySlots(?Array<googleSlot>): boolean;
  pubadsReady: boolean;
  defineSlot(string, posSizes, string): googleSlot;
  pubads(): googleService;
}

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Info to log
 * @property {object} _QP - bid value(cpm) are populated here
 * @property {object} _CV - partner specific pos id
 */

/**
 * Implements Google DFP ad server functionality
 *
 * @class
 * @exports adserver/google
 * @requires  module:coreutils
 * @requires  module:adconfig
 * @requires  module:adloader
 * @requires  module:adserverbase
 * @requires module:containerHelper
 * @requires  module:browserutil
 * @requires module:logger
 */
/**
 * @global
 * @typedef hbPosData
 * @property {string} info Info to log
 * @property {object} _QP - bid value(cpm) are populated here
 * @property {object} _CV - partner specific pos id
 */

/**
 * Implements Google DFP ad server functionality
 *
 * @class
 * @exports adserver/google
 * @requires  module:coreutils
 * @requires  module:adconfig
 * @requires  module:adloader
 * @requires  module:adserverbase
 * @requires module:containerHelper
 * @requires  module:browserutil
 * @requires module:logger
 */
class _google extends _adserverbase {
  _cVars: { [string]: CustomVars };

  _libAlreadyLoaded: boolean;

  _registeredSignalSources: boolean;

  _oldLib: string;

  _defConfig: googleASConfig;

  _config: googleASConfig;

  _targetToPos: { [string]: string };

  constructor(name: string) {
    super();
    this._name = name;
    this._defConfig = extend(true, {}, this._defConfig, {
      lib: '',
      timeout: 10000,
      url: 'https://securepubads.g.doubleclick.net/gampad/adx?'
    });
    this._config = extend(true, this._defConfig);
    this._cVars = {}; // Store position's custom vars to pass on to ad loader once ad is available
    this._libAlreadyLoaded = false;
    this._registeredSignalSources = false;
    this._oldLib = ''; // Store older lib url to prevent re-loading same lib again after updating config
    this._targetToPos = {};
  }

  /**
   * Initialize the object - called from adserver initialize
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    super.init(w, d, g);
    this._win.googletag = this._win.googletag || {
      cmd: [],
      encryptedSignalProviders: []
    };
    this._win.googletag.cmd.push(() => {
      const gtag: googleGPT = this._win.googletag;
      if (isdebugEnabled() && isFunction(gtag.openConsole)) {
        gtag.openConsole();
      }
      const events = gtag.pubads();
      events.addEventListener('slotRequested', event => {
        // Event is triggered when an ad has been requested for a slot
        const slotID = event.slot.getSlotElementId();
        const position = this._getPosFromTargetId(slotID);
        logDebug(
          `${this._name} requested  slot ID: ${slotID} Pos: ${position}`
        );
        if (position.length) {
          adloader.load(
            position,
            '',
            this._cVars[position],
            this._name,
            false,
            slotID
          );
        }
      });
      events.addEventListener('slotResponseReceived', event => {
        // Event is triggered once ad response is available
        const slotID = event.slot.getSlotElementId();
        const position = this._getPosFromTargetId(slotID);
        logDebug(
          `${this._name} response received slot ID: ${slotID} Pos: ${position}`
        );
      });

      events.addEventListener('slotRenderEnded', event => {
        // Event is triggered once the ad is rendered
        logDebug(
          `${this._name} rendered slot ID: ${event.slot.getSlotElementId()}`
        );
      });
      events.addEventListener('slotOnload', event => {
        // Event is triggered  when the creative's iframe fires its load event
        logDebug(
          `${this._name} on loading slot ID : ${event.slot.getSlotElementId()}`
        );
      });
      events.addEventListener('slotVisibilityChanged', event => {
        // Event is triggered when on-screen percentage of an ad slot's area changes
        logDebug(
          `${
            this._name
          }  Visibility Changing slot ID : ${event.slot.getSlotElementId()}`
        );
      });
      events.addEventListener('impressionViewable', event => {
        // Event is triggered when an impression becomes viewable
        logDebug(
          `${
            this._name
          }  Impression viewable slot ID : ${event.slot.getSlotElementId()}`
        );
      });
    });
    // Google DFP library handle request queue
  }

  // Register encrypted signal sources
  _registerEncryptedSignalSources() {
    const { encryptedSignalSources: essConfig = {} } = this._config || {};
    const { sources = [] } = essConfig;
    if (
      this._registeredSignalSources ||
      !this._libAlreadyLoaded ||
      sources.length === 0 ||
      !isJSONSupported() // need JSON support for encryption
    ) {
      // nothing further to do here
      return;
    }

    // _win.googletag is loaded & available.
    const gtag: googleGPT = this._win.googletag;
    if (!gtag.pubadsReady) {
      return;
    }
    logDebug(`${this._name} registering Encrypted signals`);
    // insert the handlers again
    const o = sources.flatMap(
      ({ source: arrSource = [], encrypt, encryptVersion, uolIDP }) =>
        arrSource.map(source => ({
          id: source,
          collectorFunction: () =>
            getEncryptedIDP(source, encrypt, encryptVersion, uolIDP)
        }))
    );
    o.forEach(src => gtag.encryptedSignalProviders.push(src));

    this._registeredSignalSources = true;
  }

  _preUpdateConfig() {
    this._oldLib = this._config.lib || '';
  }

  _postUpdateConfig() {
    if (!this._config.lib.length) {
      return;
    }
    // Do not load already loaded library
    if (this._libAlreadyLoaded && this._oldLib === this._config.lib) {
      return;
    }
    // load the library only for modern browsers
    if (BrowserUtil.isOldIE()) {
      return;
    }
    this._loadLibrary(this._config.lib);
    this._libAlreadyLoaded = true;
    this._registeredSignalSources = false; // to register again
  }

  clearAdRequests() {
    // reset the position specific cvars we cached
    this._cVars = {};
    this._targetToPos = {};
  }

  /**
   * Reverse lookup position name from targetId defined
   *
   * @param {string} slotID - Appnexus response object
   * @returns {string} - Position name
   * @private
   */
  _getPosFromTargetId(slotID: string = ''): string {
    return this._targetToPos[slotID] || '';
  }

  _getadUnitPath(pos: string = ''): string {
    return adconfig.getPosSettings(pos).adUnitPath || '';
  }

  _getSizes(pos: string = ''): posSizes {
    return adconfig.getPosSettings(pos).sizes || [];
  }

  /**
   * Builds iframe tag for each ad position from the qp object
   *
   * @param {string} pos - position
   * @param {object} qp - keywords
   * @returns {string} - Ad creative to render
   * @memberof _google
   */
  _getAdContent(pos: string = '', qp: QPParams = {}): string {
    const iu = this._getadUnitPath(pos);
    const c = getRandom();
    const sizes = this._getSizes(pos);
    const w = adconfig.getPosSettings(pos).width || '';
    const h = adconfig.getPosSettings(pos).height || '';
    const sz = sizes.map(v => v.join('x')).join('|');
    if (iu.length === 0 || sz.length === 0) {
      return '';
    }
    // pass the keywords as query params here
    const t = Object.entries(qp)
      .map(([k, v]) => `${k}=${String(v)}`)
      .join('&');
    const qParams = param({
      iu,
      sz,
      c,
      t
    });

    const url = `${this._config.url}${qParams}`;
    logDebug(`${this._name} IFrame URL: ${url}`);
    return `<iframe src='${url}' frameBorder=0 marginWidth=0 marginHeight=0 scrolling=no allowTransparency topmargin="0" leftmargin="0" backgroundColor="transparant" width=${w} height=${h}></iframe>`;
  }

  /**
   * Load each ad position using old script method
   *
   * @param {string} pos - position
   * @param {object} qp - keywords
   * @memberof _google
   * @returns {undefined}
   */
  loadAdsNoGPT(pos: string, qp: QPParams) {
    const adContent = this._getAdContent(pos, qp);
    if (adContent.length) {
      adloader.load(pos, adContent, this._cVars[pos], '', true);
    } else {
      logDebug(`${this._name} adcontent not avaliable`);
    }
  }

  /**
   *
   * @param {string} containerID containerID goign to be used for ad
   * @returns {boolean}
   */
  _prepareContainerDiv(containerID: string = ''): boolean {
    const div = this._doc.getElementById(containerID);
    if (!div) {
      return false;
    }
    // cleanup the container div
    cleanFrame(this._doc, div, '');
    return true;
  }

  _getPageParams(bRotation: boolean): anyObject {
    const arrParams = [this._getQueryParamsCommon(bRotation)];
    return extend(true, {}, ...arrParams);
  }

  /**
   * Loads openx static using adloader
   *
   * @param {Array<string>} posList - position list array
   * @param {object.<string, hbPosData>} o - header bidding return object
   * @param {object} customVars - custom variable to be populated inside iframe
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  loadAds(
    posList: Array<string> = [],
    o: hbPartnerData = {},
    customVars: CustomVars = {},
    bRotation: boolean = false
  ) {
    this._registerEncryptedSignalSources();
    posList.forEach(pos => {
      this._cVars[pos] = this._getCustomVars(pos, o, customVars, bRotation);
    });
    // data required for defineSlot is created as pos=>object map
    const posCIDMap: { [key: string]: string } = {};
    const qp: { [key: string]: qparams } = {};
    posList.forEach(pos => {
      // ensure that containers are created before we load ads
      containerHelper.createContainers(pos);
      // Get inactive container div of the ad position
      const adContainerFrameIds: adInfo = containerHelper.getAdContainerInfo(
        pos,
        false
      );
      this._targetToPos[adContainerFrameIds.cID] = pos;
      posCIDMap[pos] = adContainerFrameIds.cID;
      qp[pos] = this._getQueryParamsPosition(pos, o) || {};
    });

    const pageParams = this._getPageParams(bRotation);

    if (this._libAlreadyLoaded) {
      logDebug(`${this._name} queueing up ${posList.join(',')}`);
      logDebug(
        `${this._name} Containers are ${Object.values(posCIDMap).join(',')}`
      );

      // _win.googletag is loaded & available.
      const gtag: googleGPT = this._win.googletag;
      // eslint-disable-next-line no-constant-condition
      gtag.cmd.push(
        () => {
          const targetids = Object.values(posCIDMap);
          const destroySlots = gtag
            .pubads()
            .getSlots()
            .filter(v => targetids.includes(v.getSlotElementId()));
          logDebug(
            `${this._name} Destroying slots ${destroySlots
              .map(v => v.getSlotElementId())
              .join(',')}`
          );
          gtag.destroySlots(destroySlots);
        },
        () => {
          try {
            // set page level targeting before adding positions
            Object.entries(pageParams).forEach(([k, v]) =>
              gtag.pubads().setTargeting(k, v)
            );

            posList.forEach(pos => {
              const cID = posCIDMap[pos];
              const slot = gtag.defineSlot(
                this._getadUnitPath(pos),
                this._getSizes(pos),
                cID
              );
              slot.addService(gtag.pubads());
              // set targeting
              Object.entries(qp[pos]).forEach(([k, v]) =>
                slot.setTargeting(k, v)
              );
              this._prepareContainerDiv(cID);
            });

            gtag.pubads().enableSingleRequest();
            gtag.enableServices();
            posList.forEach(pos => {
              gtag.display(posCIDMap[pos]);
            });
          } catch (error) {
            logError(
              `${this._name} google library calls failure ${error.message}`
            );
          }
        }
      );
    } else {
      logDebug(`${this._name} library not loaded`);
      // fallback to iframe tag for old browser
      posList.forEach(pos => {
        // combine user IDs & QP
        const q = extend(true, {}, qp[pos], pageParams);
        this.loadAdsNoGPT(pos, q);
      });
    }
    adloader.checkQueue();
  }
}

const google: _google = new _google('googleAS');
export default google;
